/**
 * beforeunload触发条件:正常关闭标签,刷新,关闭浏览器
 */
import type { HttpWeblogInstance } from '@/types/index';
import { BasePlugin } from '../base';
import { addMonitor, removeMonitor, getMappingPage } from '@/util';

export type AutoPVFilter = (newUrl: string, oldUrl: string | null, type: 'enter' | 'leave' | 'hidden' | 'visible') => boolean;

let willunload = false;
interface Options {
    autoPVFilter: AutoPVFilter;
}

let urlChangeEventId = 'hashchange';
export default class AutoPV extends BasePlugin {
    declare weblog: HttpWeblogInstance;
    static override  key = 'autoPV';
    private beginTime = new Date().valueOf();
    private autoPVFilter: AutoPVFilter;
    private oldPushState = history.pushState;
    private oldReplaceState = history.replaceState;
    /**
     * 兼容写法，如果在 Weblog 初始化设置 plugins 时，不需要设置 weblog
     * 为兼容历史写法，这里有点乱
     * @param weblog
     * @param autoPVFilter
     */
    // 只能直接传一个方法
    constructor(autoPVFilter?: AutoPVFilter)
    // 最新用法，直接传一个带autoPVFilter的对象进来，有更多扩展可能
    constructor(
        options?: Options,
    )
    // 历史写法，将插件在weblog内部实例化
    constructor(
        weblog?: HttpWeblogInstance,
        options?: {autoPVFilter?: AutoPVFilter},
    )
    constructor(
        weblog?: HttpWeblogInstance | Options | AutoPVFilter,
        options?: {autoPVFilter?: AutoPVFilter},
    ) {
        super();
        this.autoPVFilter = () => true;
        // 兼容处理
        if (weblog) {
            if (typeof weblog === 'function') {
                this.autoPVFilter = weblog as AutoPVFilter;
            } else if (typeof (weblog as HttpWeblogInstance)?.collect === 'function') {
                if (options?.autoPVFilter) {
                    this.autoPVFilter = options.autoPVFilter;
                }
                this.apply(weblog as HttpWeblogInstance);
            } else if ('autoPVFilter' in weblog && typeof weblog.autoPVFilter === 'function') {
                this.autoPVFilter = weblog.autoPVFilter;
            }
        }
        if (options && typeof options.autoPVFilter === 'function') {
            this.autoPVFilter = options.autoPVFilter;
        }
        history.pushState = this.proxyPushState;
        history.replaceState = this.proxyReplaceState;
    }

    override apply(weblog: HttpWeblogInstance) {
        if (!weblog || !weblog.sendImmediately) return;
        this.weblog = weblog;
        if (this.autoPVFilter(location.href, null, 'enter')) {
            this.weblog.sendImmediately('PV', {
                type: 'enter',
                // @ts-ignore
                auto: true
            });
        }
        const supportsPushState = weblog.Utils.ua.supportsPushState;
        urlChangeEventId = supportsPushState() ? 'popstate' : 'hashchange';
        addMonitor(window, urlChangeEventId, this.urlChange);
        addMonitor(document, 'visibilitychange', this.visibilityChange);
    }

    private visibilityChange = () => {
        const state = document.visibilityState;
        if (state === 'visible') {
            willunload = false;
        }
        // 页面关闭也会触发 hidden 事件，这种情况下不再上报事件
        if (!this.weblog || willunload) return;
        if (this.autoPVFilter(location.href, null, state)) {
            if (state === 'hidden' || state === 'visible') {
                if (state === 'visible') this.beginTime = new Date().valueOf();
                this.weblog.sendImmediately('PV', {
                    type: state,
                    // @ts-ignore
                    auto: true,
                    beginTime: this.beginTime
                });
            }
        }
    }

    private proxyPushState = (data: any, title: string, url?: string | null | undefined) => {
        this.urlChange();
        return this.oldPushState.call(history, data, title, url);
    }

    private proxyReplaceState = (data: any, title: string, url?: string | null | undefined) => {
        this.urlChange();
        return this.oldReplaceState.call(history, data, title, url);
    }

    private urlChange = () => {
        if (!this.weblog) return;
        setTimeout(() => {
            const { page, params } = getMappingPage({url: location.href}, this.weblog.logConfig.urlMap);
            if (page && page === this.weblog.currentUrlPackage.page) {
                // @ts-ignore
                this.weblog.currentUrlPackage.update(page, params);
                return;
            };
            if (this.autoPVFilter(location.href, this.weblog.currentUrlPackage.page, 'leave')) {
                // 触发旧页面离开
                this.weblog.sendImmediately('PV', {
                    type: 'leave',
                    // @ts-ignore
                    auto: true,
                    beginTime: this.beginTime,
                    // @ts-ignore
                    urlPage: this.weblog.currentUrlPackage.toJSON()
                });
            }

            if (this.autoPVFilter(location.href, this.weblog.currentUrlPackage.page, 'enter')) {
                // 更新当前页面信息
                this.weblog.updateReferUrlPackage(this.weblog.currentUrlPackage);
                this.weblog.updateCurrentUrlPackage(location.href);
                if (this.weblog.currentUrlPackage.page.indexOf('http') === 0) {
                    console.warn('[warn 302]', `autoPV 插件 - 上报 page 信息不符合规范，可以使用 urlMap 将 url 转换为英文标识!`);
                }
                // 更新新页面 enter 时间
                this.beginTime = new Date().valueOf();
                this.weblog.sendImmediately('PV', {
                    type: 'enter',
                    // @ts-ignore
                    auto: true,
                });
            }
        }, 0);
    }

    override beforeUnload = () => {
        willunload = true;
        if (!this.weblog || !this.autoPVFilter(location.href, null, 'leave')) {
            return;
        }
        this.weblog.sendImmediately('PV', {
            type: 'leave',
            // @ts-ignore
            auto: true,
            beginTime: this.beginTime
        });
    }

    destroy() {
        removeMonitor(window, urlChangeEventId, this.urlChange);
        removeMonitor(document, 'visibilitychange', this.visibilityChange);
        history.pushState = this.oldPushState;
        history.replaceState = this.oldReplaceState;
    }
}
